import { Anima } from "kido/anima";
import { App, type GpuInstance } from "@app/context";
import { PageManager } from "@app/page-manager";

const _FX_D = 600;
const _FX_E_OUT = 'ioW';
const _FX_E_IN = 'ioW';

/** Cache DOM refs for .t and .t-scrim overlays */
function _els() {
  return {
    t: document.querySelector<HTMLElement>('.t'),
    scrim: document.querySelector<HTMLElement>('.t-scrim'),
  };
}

/** Whether the current transition should use .t / .t-scrim */
function _useT(): boolean {
  const fromPage = App.route.old.page;
  const toPage = App.route.new.page;

  // Skip for projects→project (has its own visual transition)
  if (fromPage === 'projects' && toPage === 'project') return false;
  // Skip for play→play-detail
  if (fromPage === 'play' && toPage === 'play-detail') return false;
  // Skip for home→projects via scroll (transitState signals scroll-triggered)
  if (fromPage === 'home' && toPage === 'projects' && App.transitState?.direction === 'to-projects') return false;

  return true;
}

export const Transition = {
  async out(): Promise<void> {
    const gpu = App.gpu as GpuInstance | null;
    const fromUrl = App.route.old.url;
    const toUrl = App.route.new.url;

    // GPU prep — snapshot current scene before DOM swap
    if (gpu && fromUrl && toUrl) {
      try {
        gpu.scene?.collectDomSlots?.();
        gpu.scene?.updateBounds?.();
      } catch { }
      gpu.prepareTransition?.(fromUrl, toUrl);
    }

    App.scroller?.pause();

    const useT = _useT();

    // Skip visual out for seamless scroll transition (home→projects via scroll)
    const isScrollHomeToProjects =
      App.route.old.page === 'home' && App.route.new.page === 'projects'
      && App.transitState?.direction === 'to-projects';
    if (isScrollHomeToProjects) return;

    const isProjectsToProject =
      App.route.old.page === 'projects' && App.route.new.page === 'project';
    const isPlayToPlayDetail =
      App.route.old.page === 'play' && App.route.new.page === 'play-detail';
    if (isProjectsToProject || isPlayToPlayDetail) {
      const app = App.app as HTMLElement | null;
      if (app) app.style.opacity = '0';
      return;
    }

    // Transitions that skip .t still need to fade #app
    if (!useT) {
      const app = App.app as HTMLElement | null;
      const planes = (gpu?.scene as any)?.planes ?? [];

      await new Promise<void>((resolve) => {
        if (app) {
          new Anima({
            el: app,
            p: { o: [1, 0, ''] },
            d: 800,
            e: _FX_E_IN,
            cb: resolve,
          }).play();
        } else {
          resolve();
        }

        for (const plane of planes) {
          if (!plane?.boundsPx) continue;
          const startO = plane.opacity ?? 1;
          new Anima({
            d: _FX_D,
            e: _FX_E_IN,
            u: (v: { prE: number }) => {
              plane.opacity = startO * (1 - v.prE);
              gpu?.requestRedraw?.();
            },
            cb: () => { plane.opacity = 0; },
          }).play();
        }
      });
      return;
    }

    // Animate .t slide up from bottom + .t-scrim fade in
    const { t, scrim } = _els();

    await new Promise<void>((resolve) => {
      if (t) {
        new Anima({
          el: t,
          p: { y: [100, 0, '%'] },
          d: 800,
          e: _FX_E_IN,
          cb: resolve,
        }).play();
      } else {
        resolve();
      }

      if (scrim) {
        new Anima({
          el: scrim,
          p: { o: [0, .4, ''] },
          d: 800,
          e: _FX_E_IN,
        }).play();
      }
    });
  },

  async in(): Promise<void> {
    const gpu = App.gpu as GpuInstance | null;
    const isToHome = App.route.new.page === 'home';
    const isToProjects = App.route.new.page === 'projects';
    const isToPlay = App.route.new.page === 'play';
    const isToProjectFromProjects =
      App.route.new.page === 'project' && App.route.old.page === 'projects';
    const isToPlayDetailFromPlay =
      App.route.new.page === 'play-detail' && App.route.old.page === 'play';
    const hasOwnIn = isToHome || isToProjects || isToPlay || isToProjectFromProjects || isToPlayDetailFromPlay;

    const useT = _useT();

    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Reinit video planes for the new scene
    const from = (gpu?.transition as any)?.fromScene ?? null;
    const to = (gpu?.transition as any)?.toScene ?? null;
    try {
      if (from && to && from === to) {
        // shared scene — skip reinit
      } else if (to) {
        to.reinitVideoPlanes?.();
      } else if (gpu?.scene) {
        gpu.scene.reinitVideoPlanes?.();
      }
    } catch { }

    // Freeze bounds before commit for pages with own in animation
    if (hasOwnIn) {
      gpu?.freezeBounds?.(true);
    }

    // Commit GPU scene swap
    gpu?.commitTransition?.();

    // Re-collect DOM slots (needed by page init to read domSlots array)
    try {
      gpu?.scene?.collectDomSlots?.();
    } catch { }

    // Update bounds and render — skip for pages with own in animation
    if (!hasOwnIn) {
      gpu?.freezeBounds?.(false);
      try {
        gpu?.scene?.updateBounds?.();
      } catch { }
      try {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        gpu?.requestRedraw?.();
      } catch { }
    }

    // For projects→project, set videosPlaying flag before page init
    if (isToProjectFromProjects && gpu) {
      gpu.videosPlaying = true;
    }

    // Page init
    try {
      await PageManager.initPage();
    } catch { }

    // Resume videos after page init
    gpu?.setVideosPlaying?.(true);

    // Bounds update after init — skip for pages with own in
    if (!hasOwnIn) {
      try {
        gpu?.scene?.collectDomSlots?.();
        gpu?.scene?.updateBounds?.();
        gpu?.requestRedraw?.();
      } catch { }
    }

    // Wait for video to be ready before revealing (behind .t)
    if (useT && isToHome && gpu) {
      const homeScene = gpu.scenes['/'] as any;
      if (homeScene?.videoReady) {
        try { await homeScene.videoReady; } catch { }
      }
      gpu.requestRedraw?.();
    }

    // Pages with own in animation: reset #app opacity and let page handle visuals
    if (hasOwnIn) {
      const app = App.app as HTMLElement | null;
      if (app) app.style.opacity = '1';

      if (useT) {
        const { gate, done } = _slideOut();
        await gate;
        PageManager.pageIn().catch(() => { });
        await done;
      } else {
        try { await PageManager.pageIn(); } catch { }
      }
      return;
    }

    // Standard in: page init done, then reveal
    if (useT) {
      const { gate, done } = _slideOut();
      await gate;
      PageManager.pageIn().catch(() => { });
      await done;
    } else {
      try { await PageManager.pageIn(); } catch { }
      // Fade #app back in for transitions without .t
      const app = App.app as HTMLElement | null;
      if (app) {
        await new Promise<void>((resolve) => {
          new Anima({
            el: app,
            p: { o: [0, 1, ''] },
            d: _FX_D,
            e: _FX_E_OUT,
            cb: resolve,
          }).play();
        });
      }
    }

    App.scroller?.resume?.();
  },
};

/** Animate .t slide up off screen + .t-scrim fade out.
 *  Returns gate (resolves at 90% progress) and done (resolves on completion). */
function _slideOut(): { gate: Promise<void>; done: Promise<void> } {
  const { t, scrim } = _els();
  let gateResolve: () => void;
  let gated = false;
  const gate = new Promise<void>((r) => { gateResolve = r; });

  const done = new Promise<void>((resolve) => {
    if (t) {
      new Anima({
        el: t,
        p: { y: [0, -100, '%'] },
        d: 800,
        de: 200,
        e: _FX_E_OUT,
        u: (v: { prE: number }) => {
          t.style.transform = `translate3d(0, ${-100 * v.prE}%, 0)`;
          if (!gated && v.prE >= 0.9) {
            gated = true;
            gateResolve();
          }
        },
        cb: () => {
          if (!gated) gateResolve();
          t.style.transform = 'translate3d(0, 100%, 0)';
          resolve();
        },
      }).play();
    } else {
      gateResolve!();
      resolve();
    }

    if (scrim) {
      new Anima({
        el: scrim,
        p: { o: [.4, 0, ''] },
        d: 800,
        de: 200,
        e: _FX_E_OUT,
      }).play();
    }
  });

  return { gate, done };
}
